import { FC, ReactNode, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { twMerge } from 'tailwind-merge';
import { useNavigate } from 'react-router-dom';

import { replacePathParams } from '@/common/utils/utils';
import LeftArrowIcon from '@/components/BackArrowIcon';
import useIsMobileView from '@/hooks/useIsMobileView';
import SitOutButton from '@/pages/table/components/SitOutButton/SitOutButton';
import TableHeaderButton from '@/pages/table/components/TableHeaderButton/TableHeaderButton';
import ROUTES from '@/routers/Routes';
import { ReducerState } from '@/services/common/lib/reducerState';
import useGetTableHandHistory from '@/services/table/queries/useGetTableHandHistory';
import { showTableModal, tableRefetchHistory } from '@/services/table/tableActions';
import { TableModalTypes, PlayerSeatDto } from '@/services/table/tableState';
import RebuyButton from '../RebuyButton/RebuyButton';
import TableTabs from '../TableTab/TableTabs';
import styles from './TableHeader.module.css';
import TableHeaderIconButtons from '../TableHeaderIconButtons/TableHeaderIconButtons';
import MobileHeaderMenu from '../MobileHeaderMenu/MobileHeaderMenu';
import TableInfoHeader from '../TableInfoHeader/TableInfoHeader';
import useGetUserTables from '@/services/table/queries/useGetUserTables';

interface TableHeaderParams {
  userId: string;
  selectedTableId: string;
  showChat: boolean;
  setShowChat: (showChat: boolean) => void;
  game: string | undefined;
  stakes: string | ReactNode;
  playerSeat: PlayerSeatDto | undefined; //TODO check this field
  tableId: string;
  tableTabsInDesktopEnabled: boolean;
}

const TableHeader: FC<TableHeaderParams> = ({
  userId,
  selectedTableId,
  showChat,
  setShowChat,
  game,
  stakes,
  tableId,
  playerSeat,
  tableTabsInDesktopEnabled,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isMobileView = useIsMobileView();
  const { data: handHistory, refetch } = useGetTableHandHistory(tableId ?? '');
  const player = useSelector((state: ReducerState) => state.players[tableId]);

  useEffect(() => {
    if (player && player.refetchHandHistory) {
      refetch();
      dispatch(tableRefetchHistory(tableId));
    }
  }, [player?.refetchHandHistory]);

  const tableState = useSelector((state: ReducerState) => state.tables?.[tableId]);

  const onClickShowChat = () => setShowChat(!showChat);
  const onClickGoBackHandler = () => navigate(ROUTES.LOBBY);
  const onClickShowTableInfo = () => {
    if (isMobileView) {
      setShowChat(false);
    }
    dispatch(showTableModal(tableId, TableModalTypes.TABLE_INFO));
  };

  const onClickShowLeaveTable = () => {
    if (isMobileView) {
      setShowChat(false);
    }
    dispatch(showTableModal(tableId, TableModalTypes.LEAVE_TABLE));
  };

  const { data: userTablesData } = useGetUserTables({
    userId,
    tableUrl: tableState?.url,
  });

  const tables = userTablesData?.data;

  const activeTables = tables?.filter((table) => table.tableId !== selectedTableId);

  const isSitout = playerSeat?.active === false;

  const navigateTableHistoryHandler = () => {
    if (handHistory) {
      const handId = handHistory[handHistory.length - 1]?.handId || '';
      const width = window.innerWidth + 100;
      const height = window.innerHeight + 100;
      const url = replacePathParams(ROUTES.REPLAY, { tableId: tableId || '', handId });
      window.open(url, '_blank', `width=${width},height=${height},resizable=yes,scrollbars=yes,status=yes`);
    }
  };

  return (
    <div className="z-20 w-full text-[16px]">
      <header className={twMerge(styles.header, 'has-[#menu-button[data-open]]:bg-ph-background-dark')}>
        {/* {isMobileView && (
          <div className={`mr-4 flex w-10 items-center justify-start rounded-lg ${styles.headerButtonBack}`}>
            <button onClick={onClickGoBackHandler} className="arrow">
              <LeftArrowIcon className="w-6 text-ph-gray-silver hover:text-ph-gray-white lg:w-10" />
            </button>
          </div>
        )} */}
        {!isMobileView && <TableInfoHeader game={game} stakes={stakes} />}
        {!isMobileView && tableTabsInDesktopEnabled && activeTables && activeTables.length > 0 && (
          <div className="mx-4 flex-1 overflow-x-auto">
            <TableTabs selectedTableId={selectedTableId} tables={tables} userId={userId} />
          </div>
        )}
        {!isMobileView && (
          <div className="z-10 flex flex-shrink-0 items-center justify-center gap-2 md:gap-4">
            {playerSeat && !isSitout && <SitOutButton tableId={tableId} />}
            <RebuyButton tableId={tableId} seat={playerSeat?.seat} disabled={playerSeat?.newPlayer} />
            <TableHeaderIconButtons
              tableId={tableId}
              onClickShowTableInfo={onClickShowTableInfo}
              onClickShowChat={onClickShowChat}
              onClickShowLeaveTable={onClickShowLeaveTable}
              onSettingsOpen={() => isMobileView && setShowChat(false)}
              showChat={showChat}
            />
          </div>
        )}
        {isMobileView && (
          <div className="flex w-full items-center gap-4">
            <div className={`flex w-10 items-center justify-start rounded-lg ${styles.headerButtonBack}`}>
              <button onClick={onClickGoBackHandler} className="arrow">
                <LeftArrowIcon className="w-6 text-ph-gray-silver hover:text-ph-gray-white lg:w-10" />
              </button>
            </div>
            <div className="flex-1">
              {activeTables && activeTables.length > 0 ? (
                <TableTabs
                  selectedTableId={selectedTableId}
                  tables={tables}
                  userId={userId}
                  className="max-w-[calc(100vw-46px-52px-32px-16px)]"
                />
              ) : (
                <TableInfoHeader game={game} stakes={stakes} />
              )}
            </div>
            <MobileHeaderMenu
              tableId={tableId}
              onClickShowTableInfo={onClickShowTableInfo}
              onClickShowChat={onClickShowChat}
              onClickShowLeaveTable={onClickShowLeaveTable}
              onSettingsOpen={() => isMobileView && setShowChat(false)}
              onClickHandHistory={navigateTableHistoryHandler}
              showChat={showChat}
              playerSeat={playerSeat}
            />
          </div>
        )}
      </header>
      {!isMobileView && (
        <TableHeaderButton disabled={!(handHistory && handHistory.length >= 1)} onClick={navigateTableHistoryHandler}>
          Hand History
        </TableHeaderButton>
      )}
    </div>
  );
};

export default TableHeader;
